import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatFCFA, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2 } from "lucide-react";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [doubleConfirm, setDoubleConfirm] = useState(false);

  const load = () =>
    api.get("/admin/products").then((r) => setProducts(r.data));

  useEffect(() => { load(); }, []);

  const handleDelete = async () => {
    if (!confirmDelete || !doubleConfirm) return;
    try {
      await api.delete(`/admin/products/${confirmDelete.id}`);
      toast.success("Produit supprimé");
      setConfirmDelete(null);
      setDoubleConfirm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl">Produits</h1>
          <p className="text-xs uppercase tracking-widest text-[#6B6B6B] mt-1">
            {products.length} produit{products.length > 1 ? "s" : ""}
          </p>
        </div>
        <Link to="/admin/produits/nouveau" className="btn-primary" data-testid="new-product-btn">
          <Plus size={14} /> Nouveau produit
        </Link>
      </div>

      <div className="border border-black/5 bg-white" data-testid="admin-products-table">
        <table className="w-full text-sm">
          <thead className="border-b border-black/5">
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6B6B6B]">
              <th className="px-4 py-3">Image</th>
              <th className="px-4 py-3">Nom</th>
              <th className="px-4 py-3">Catégorie</th>
              <th className="px-4 py-3">Prix</th>
              <th className="px-4 py-3">Revient</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center py-12 text-[#6B6B6B]">Aucun produit. Créez le premier.</td>
              </tr>
            )}
            {products.map((p) => (
              <tr key={p.id} className="border-b border-black/5 hover:bg-[#B8860B]/[0.02]">
                <td className="px-4 py-3">
                  {p.photos?.[0]?.path ? (
                    <img src={fileUrl(p.photos[0].path)} className="w-12 h-12 object-cover" alt="" />
                  ) : (
                    <div className="w-12 h-12 bg-[#FAF7F0] flex items-center justify-center text-[#B8860B]/40 font-display">✟</div>
                  )}
                </td>
                <td className="px-4 py-3 text-[#1A1A1A]">
                  {p.is_favorite && <span title="Coup de cœur du mois" className="text-[#B8860B] mr-1">★</span>}
                  {p.name}
                  {p.sku && <div className="text-[10px] text-[#6B6B6B] uppercase tracking-widest mt-0.5 font-mono">{p.sku}</div>}
                </td>
                <td className="px-4 py-3 text-[#6B6B6B]">{p.category}</td>
                <td className="px-4 py-3 text-[#B8860B]">{formatFCFA(p.price)}</td>
                <td className="px-4 py-3 text-[#6B6B6B]">{formatFCFA(p.cost_price)}</td>
                <td className={`px-4 py-3 ${p.stock < 2 ? "text-red-400" : p.stock < 5 ? "text-orange-400" : "text-[#1A1A1A]"}`}>
                  {p.stock}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Link to={`/admin/produits/${p.id}`} className="p-2 hover:text-[#B8860B]" data-testid={`edit-product-${p.id}`}>
                      <Edit size={14} />
                    </Link>
                    <button
                      className="p-2 hover:text-red-400"
                      onClick={() => { setConfirmDelete(p); setDoubleConfirm(false); }}
                      data-testid={`delete-product-${p.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-6" data-testid="delete-modal">
          <div className="max-w-md w-full bg-white border border-red-500/40 p-8">
            <h3 className="font-display text-2xl text-red-400">Confirmation de suppression</h3>
            <p className="text-sm text-[#1A1A1A]/75 mt-4">
              Vous êtes sur le point de supprimer définitivement <strong>{confirmDelete.name}</strong>.
            </p>
            {!doubleConfirm ? (
              <div className="mt-6 flex gap-3">
                <button className="btn-secondary flex-1" onClick={() => setConfirmDelete(null)}>Annuler</button>
                <button className="btn-primary flex-1 !bg-red-700 !border-red-700 !text-white" onClick={() => setDoubleConfirm(true)} data-testid="confirm-delete-1">
                  Confirmer
                </button>
              </div>
            ) : (
              <div className="mt-6">
                <p className="text-xs text-red-400 mb-4">Double confirmation requise — cette action est irréversible.</p>
                <div className="flex gap-3">
                  <button className="btn-secondary flex-1" onClick={() => { setConfirmDelete(null); setDoubleConfirm(false); }}>Annuler</button>
                  <button className="btn-primary flex-1 !bg-red-700 !border-red-700 !text-white" onClick={handleDelete} data-testid="confirm-delete-2">
                    Supprimer définitivement
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
